import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { formatCurrency, formatCurrencyCompact } from '../../../utils/CurrencyFormat';
import { formatDateForPdf } from '../../../utils/pdfFormat';
import {
  addPartnerLendingAllocation,
  addPartnerLendingPayout,
  deletePartnerLendingAllocation,
  deletePartnerLendingPayout,
  getPartnerControlSnapshot,
  getPartnerLendingPayoutSummary,
  getPartnerPortalControlSnapshot,
  getPartnerPortalLendingAllocations,
  getPartnerPortalLendingPayoutSummary,
  getPartnerPortalLendingPayouts,
  listPartnerCapitalDeployments,
  listPartnerLendingAllocations,
  listPartnerLendingPayouts,
  type PartnerLendingAllocation,
  type PartnerLendingPayout,
  type PartnerLendingPayoutSummary,
} from '../../../services/request/CapitalAnalytics';
import { getAllDebits } from '../../../services/request/Debits';
import type { PartnerCapitalDeployment, PartnerControlSnapshot } from '../../../models/CapitalAnalyticsTypes';

const fmt = (n: number) => formatCurrency(n, 'INR', true);
const fmtCompact = (n: number) => formatCurrencyCompact(n, 'INR', true);

interface DebitOption {
  id: string;
  label: string;
  outstanding: number;
  interestRate: number;
}

export interface PartnerLendingVisibilityPanelProps {
  partnerId?: string;
  partnerName?: string;
  showOperatorEconomics?: boolean;
  allowManage?: boolean;
  /** Use partner portal auth endpoints (read-only). */
  portalMode?: boolean;
  onChanged?: () => void;
}

const currentPeriodKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const PartnerLendingVisibilityPanel: React.FC<PartnerLendingVisibilityPanelProps> = ({
  partnerId,
  showOperatorEconomics = true,
  allowManage = false,
  portalMode = false,
  onChanged,
}) => {
  const [loading, setLoading] = useState(true);
  const [snapshot, setSnapshot] = useState<PartnerControlSnapshot | null>(null);
  const [deployments, setDeployments] = useState<PartnerCapitalDeployment[]>([]);
  const [linkedDebits, setLinkedDebits] = useState<PartnerLendingAllocation[]>([]);
  const [payouts, setPayouts] = useState<PartnerLendingPayout[]>([]);
  const [payoutSummary, setPayoutSummary] = useState<PartnerLendingPayoutSummary | null>(null);
  const [debitOptions, setDebitOptions] = useState<DebitOption[]>([]);
  const [debitId, setDebitId] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [payoutAllocationId, setPayoutAllocationId] = useState('');
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutDate, setPayoutDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [payoutPeriod, setPayoutPeriod] = useState(currentPeriodKey);
  const [payoutNote, setPayoutNote] = useState('');
  const [payoutSaving, setPayoutSaving] = useState(false);

  const load = useCallback(async () => {
    if (!partnerId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      if (portalMode) {
        const [snap, allocations, payoutRows, summary] = await Promise.all([
          getPartnerPortalControlSnapshot(),
          getPartnerPortalLendingAllocations(),
          getPartnerPortalLendingPayouts(),
          getPartnerPortalLendingPayoutSummary(),
        ]);
        setSnapshot(snap);
        setDeployments([]);
        setLinkedDebits(allocations);
        setPayouts(payoutRows);
        setPayoutSummary(summary);
      } else {
        const [snap, lends, allocations, payoutRows, summary] = await Promise.all([
          getPartnerControlSnapshot(partnerId),
          listPartnerCapitalDeployments({ partnerId, use: 'LENDING' }),
          listPartnerLendingAllocations(partnerId),
          listPartnerLendingPayouts(partnerId),
          getPartnerLendingPayoutSummary(partnerId),
        ]);
        setSnapshot(snap);
        setDeployments(lends);
        setLinkedDebits(allocations);
        setPayouts(payoutRows);
        setPayoutSummary(summary);
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Failed to load lending data');
    } finally {
      setLoading(false);
    }
  }, [partnerId, portalMode]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!allowManage) return;
    getAllDebits(0, 100, { status: 'ACTIVE' })
      .then((res: any) => {
        const rows = res?.result ?? res?.data?.result ?? [];
        setDebitOptions(
          rows.map((d: any) => ({
            id: d.id,
            label: `${d.borrowerName ?? 'Borrower'} · ${d.type ?? 'DEBIT'} · outstanding ${formatCurrency(d.outstanding ?? 0)}`,
            outstanding: d.outstanding ?? 0,
            interestRate: d.interestRate ?? 2,
          })),
        );
      })
      .catch(() => setDebitOptions([]));
  }, [allowManage]);

  const handleAdd = async () => {
    if (!partnerId || !debitId || !amount) return;
    setSaving(true);
    try {
      await addPartnerLendingAllocation(partnerId, {
        debitId,
        allocatedAmount: Number(amount),
        note: note || undefined,
        partnerRatePctMonthly: 1.5,
      });
      toast.success('Partner capital deployed to lending');
      setAmount('');
      setNote('');
      setDebitId('');
      await load();
      onChanged?.();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Failed to allocate');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (deploymentId: string) => {
    if (!partnerId) return;
    try {
      await deletePartnerLendingAllocation(partnerId, deploymentId);
      toast.success('Lending allocation removed');
      await load();
      onChanged?.();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Failed to remove');
    }
  };

  const handleRecordPayout = async () => {
    if (!partnerId || !payoutAllocationId || !payoutAmount || !payoutDate) return;
    setPayoutSaving(true);
    try {
      await addPartnerLendingPayout(partnerId, {
        lendingAllocationId: payoutAllocationId,
        amount: Number(payoutAmount),
        payoutDate,
        periodKey: payoutPeriod,
        referenceNote: payoutNote || undefined,
      });
      toast.success('Partner payout recorded');
      setPayoutAllocationId('');
      setPayoutAmount('');
      setPayoutNote('');
      setPayoutPeriod(currentPeriodKey());
      await load();
      onChanged?.();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Failed to record payout');
    } finally {
      setPayoutSaving(false);
    }
  };

  const handleDeletePayout = async (payoutId: string) => {
    if (!partnerId) return;
    try {
      await deletePartnerLendingPayout(partnerId, payoutId);
      toast.success('Payout removed');
      await load();
      onChanged?.();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Failed to remove payout');
    }
  };

  if (loading) return <LinearProgress sx={{ my: 2 }} />;

  if (!partnerId) {
    return <Alert severity="info" variant="outlined">Partner id is required.</Alert>;
  }

  const hasLending = (snapshot?.lendingInvested ?? 0) > 0 || linkedDebits.length > 0;
  const lendingTotal = snapshot?.lendingInvested
    ?? linkedDebits.reduce((s, d) => s + (d.allocatedAmount ?? 0), 0);
  const partnerRate = snapshot?.partnerLendingRatePctMonthly
    ?? linkedDebits[0]?.partnerRatePctMonthly
    ?? 1.5;
  const borrowerRate = snapshot?.borrowerLendingRatePctMonthly
    ?? linkedDebits[0]?.borrowerRatePctMonthly
    ?? 2.0;
  const operatorRate = snapshot?.operatorMarginPctMonthly ?? 0.5;
  const monthlyIncome = snapshot?.monthlyPartnerLendingIncome
    ?? linkedDebits.reduce((s, d) => s + (d.monthlyPartnerIncome ?? 0), 0);
  const monthlyMargin = snapshot?.monthlyOperatorLendingMargin
    ?? Math.round(lendingTotal * (operatorRate / 100));

  return (
    <Stack spacing={2}>
      {allowManage && (
        <Box sx={{ p: 2, borderRadius: 2, border: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Typography variant="subtitle2" gutterBottom>Deploy capital to lending</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
            <TextField
              select
              size="small"
              label="Active debit"
              value={debitId}
              onChange={(e) => setDebitId(e.target.value)}
              sx={{ minWidth: 280, flex: 1 }}
            >
              <MenuItem value="">Select debit…</MenuItem>
              {debitOptions.map((d) => (
                <MenuItem key={d.id} value={d.id}>{d.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Amount (INR)"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              sx={{ width: 140 }}
            />
            <TextField
              size="small"
              label="Note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              sx={{ flex: 1 }}
            />
            <Button variant="contained" onClick={handleAdd} disabled={saving || !debitId || !amount}>
              {saving ? 'Saving…' : 'Deploy'}
            </Button>
          </Stack>
        </Box>
      )}

      {!hasLending && (
        <Alert severity="info" variant="outlined">
          No capital deployed to lending yet.
          {allowManage ? ' Link partner capital when creating a debit or use the deploy form above.' : ''}
        </Alert>
      )}

      {hasLending && (
        <>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">In lending</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#E65100' }}>
                {fmtCompact(lendingTotal)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Agreed rate</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{partnerRate}%/mo</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">
                {showOperatorEconomics ? 'Partner income/mo' : 'Est. income/mo'}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                {fmtCompact(monthlyIncome)}
              </Typography>
            </Grid>
            {payoutSummary && (
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Paid to partner</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {fmtCompact(payoutSummary.totalPaidOut)}
                </Typography>
                {payoutSummary.outstandingBalance > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Est. accrued due {fmtCompact(payoutSummary.outstandingBalance)}
                  </Typography>
                )}
              </Grid>
            )}
            {showOperatorEconomics && (
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Operator margin/mo</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {fmtCompact(monthlyMargin)}
                </Typography>
              </Grid>
            )}
          </Grid>

          <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover', border: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" gutterBottom>Lending economics</Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.75} useFlexGap>
              <Chip size="small" label={`Borrower pays ${borrowerRate}%/month`} />
              <Chip size="small" color="success" variant="outlined" label={`Partner ${partnerRate}%`} />
              {showOperatorEconomics && (
                <Chip size="small" color="primary" variant="outlined" label={`Operator ${operatorRate}%`} />
              )}
            </Stack>
          </Box>

          {linkedDebits.length > 0 && (
            <>
              <Typography variant="subtitle2">Debits funded with partner capital</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {showOperatorEconomics && <TableCell>Borrower</TableCell>}
                      <TableCell>Debit</TableCell>
                      <TableCell align="right">Principal</TableCell>
                      <TableCell align="right">Outstanding</TableCell>
                      <TableCell align="right">Deployed</TableCell>
                      <TableCell align="right">Partner rate</TableCell>
                      <TableCell align="right">Income/mo</TableCell>
                      <TableCell align="right">Paid out</TableCell>
                      <TableCell>Maturity</TableCell>
                      <TableCell>Status</TableCell>
                      {allowManage && <TableCell width={48} />}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {linkedDebits.map((row) => (
                      <TableRow key={row.id ?? row.debitId}>
                        {showOperatorEconomics && (
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {row.borrowerName ?? '—'}
                            </Typography>
                            {row.borrowerCity && (
                              <Typography variant="caption" color="text.secondary">{row.borrowerCity}</Typography>
                            )}
                          </TableCell>
                        )}
                        <TableCell>
                          <Typography variant="body2">{row.debitType ?? 'DEBIT'}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            since {row.allocatedAt ? formatDateForPdf(row.allocatedAt) : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{fmt(row.debitAmount ?? 0)}</TableCell>
                        <TableCell align="right">{fmt(row.debitOutstanding ?? 0)}</TableCell>
                        <TableCell align="right">{fmt(row.allocatedAmount)}</TableCell>
                        <TableCell align="right">{row.partnerRatePctMonthly ?? partnerRate}%</TableCell>
                        <TableCell align="right">{fmt(row.monthlyPartnerIncome ?? 0)}</TableCell>
                        <TableCell align="right">
                          {fmt(row.totalPaidOut ?? 0)}
                          {row.lastPayoutDate && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              last {formatDateForPdf(row.lastPayoutDate)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{row.debitMaturityDate ? formatDateForPdf(row.debitMaturityDate) : '—'}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.debitStatus ?? 'ACTIVE'}
                            color={row.debitStatus === 'WATCH' ? 'warning' : row.debitStatus === 'MATURED' ? 'default' : 'success'}
                            variant="outlined"
                          />
                        </TableCell>
                        {allowManage && row.id && (
                          <TableCell align="right">
                            <IconButton size="small" color="error" onClick={() => handleDelete(row.id!)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {allowManage && linkedDebits.length > 0 && (
            <Box sx={{ p: 2, borderRadius: 2, border: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
              <Typography variant="subtitle2" gutterBottom>Record partner payout</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Pay partner their share based on capital deployed on a linked debit. Amount defaults to monthly income for the selected allocation.
              </Typography>
              <Grid container spacing={1} alignItems="flex-start">
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Linked debit"
                    value={payoutAllocationId}
                    onChange={(e) => {
                      setPayoutAllocationId(e.target.value);
                      const alloc = linkedDebits.find((a) => a.id === e.target.value);
                      setPayoutAmount(alloc?.monthlyPartnerIncome ? String(Math.round(alloc.monthlyPartnerIncome)) : '');
                    }}
                  >
                    <MenuItem value="">Select allocation…</MenuItem>
                    {linkedDebits.map((a) => (
                      <MenuItem key={a.id} value={a.id ?? ''}>
                        {a.borrowerName ?? 'Borrower'} · {a.debitType ?? 'DEBIT'} · {fmt(a.allocatedAmount)} @ {a.partnerRatePctMonthly ?? partnerRate}%
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Period"
                    placeholder="2026-06"
                    value={payoutPeriod}
                    onChange={(e) => setPayoutPeriod(e.target.value)}
                  />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Payout date"
                    type="date"
                    value={payoutDate}
                    onChange={(e) => setPayoutDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Amount (INR)"
                    type="number"
                    value={payoutAmount}
                    onChange={(e) => setPayoutAmount(e.target.value)}
                  />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Reference"
                    value={payoutNote}
                    onChange={(e) => setPayoutNote(e.target.value)}
                    placeholder="NEFT ref"
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleRecordPayout}
                    disabled={payoutSaving || !payoutAllocationId || !payoutAmount}
                  >
                    {payoutSaving ? 'Saving…' : 'Record payout'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}

          {payouts.length > 0 && (
            <>
              <Typography variant="subtitle2">Payout history</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Period</TableCell>
                      <TableCell>Payout date</TableCell>
                      {showOperatorEconomics && <TableCell>Borrower / debit</TableCell>}
                      {!showOperatorEconomics && <TableCell>Debit</TableCell>}
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Reference</TableCell>
                      {allowManage && <TableCell width={48} />}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payouts.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell>{p.periodKey}</TableCell>
                        <TableCell>{formatDateForPdf(p.payoutDate)}</TableCell>
                        <TableCell>
                          {showOperatorEconomics && (
                            <Typography variant="body2">{p.borrowerName ?? '—'}</Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">{p.debitType ?? 'DEBIT'}</Typography>
                        </TableCell>
                        <TableCell align="right">{fmt(p.amount)}</TableCell>
                        <TableCell>{p.referenceNote || '—'}</TableCell>
                        {allowManage && p.id && (
                          <TableCell align="right">
                            <IconButton size="small" color="error" onClick={() => handleDeletePayout(p.id!)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {deployments.length > 0 && linkedDebits.length === 0 && (
            <>
              <Typography variant="subtitle2">Deployments (legacy view)</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Debit</TableCell>
                      {showOperatorEconomics && <TableCell>Borrower</TableCell>}
                      <TableCell align="right">Deployed</TableCell>
                      <TableCell align="right">Partner rate</TableCell>
                      <TableCell>Maturity</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {deployments.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.schemeCode}</TableCell>
                        {showOperatorEconomics && <TableCell>{row.borrowerName ?? '—'}</TableCell>}
                        <TableCell align="right">{fmt(row.capitalDeployed)}</TableCell>
                        <TableCell align="right">{row.partnerSharePctMonthly ?? partnerRate}%</TableCell>
                        <TableCell>{row.maturityDate ? formatDateForPdf(row.maturityDate) : '—'}</TableCell>
                        <TableCell>{row.status}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {snapshot?.nextMaturityDays != null && (
            <Typography variant="caption" color="text.secondary">
              Next lending maturity in {snapshot.nextMaturityDays} day{snapshot.nextMaturityDays === 1 ? '' : 's'}.
            </Typography>
          )}
        </>
      )}
    </Stack>
  );
};

export default PartnerLendingVisibilityPanel;
